import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Plus,
  Shield,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useAllExams } from "@/hooks/use-exams";
import {
  useAdminUsers,
  useAssignExam,
  usePendingExamRequests,
  useRevokeExam,
  useReviewExamRequest,
  useSetUserSuspension,
} from "@/hooks/use-admin";
import { IPBlockPanel } from "@/components/admin/ip-block-panel";
import { SuspendedUsersPanel } from "@/components/admin/suspended-users-panel";
import { createErrorMessage } from "@/lib/error-message";
import { getCategories } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import type { AdminUser, Category, Exam } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExamPicker } from "@/components/exam-picker";

export const Route = createFileRoute("/_main/admin")({
  component: AdminPage,
});

type RevokeTarget = {
  userID: string;
  userEmail: string;
  examSlug: string;
  examName: string;
};

function AdminPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { t } = useLanguage();

  const enabled = isAuthenticated && user?.role === "admin";

  const { data: users = [], isLoading: usersLoading, error: usersError } =
    useAdminUsers();
  const { data: exams = [], isLoading: examsLoading, error: examsError } =
    useAllExams();
  const {
    data: requests = [],
    isLoading: requestsLoading,
    error: requestsError,
  } = usePendingExamRequests();

  const categoryQueries = useQueries({
    queries: exams.map((exam) => ({
      queryKey: queryKeys.categories.list(exam.id),
      queryFn: () => getCategories(exam.id),
      staleTime: 5 * 60 * 1000,
      enabled,
    })),
  });

  const categoryMap = useMemo<Record<string, Category[]>>(() => {
    const map: Record<string, Category[]> = {};
    exams.forEach((exam, i) => {
      map[exam.id] = categoryQueries[i]?.data ?? [];
    });
    return map;
  }, [exams, categoryQueries]);

  const examMap = useMemo<Record<string, Exam>>(
    () => Object.fromEntries(exams.map((e) => [e.id, e])),
    [exams],
  );

  const assignMutation = useAssignExam();
  const suspendMutation = useSetUserSuspension();
  const [suspendTarget, setSuspendTarget] = useState<AdminUser | null>(null);
  const [suspendReason, setSuspendReason] = useState("");
  const suspendedCount = users.filter((u) => u.suspended).length;
  function openSuspendDialog(u: AdminUser) {
    setSuspendReason("");
    setSuspendTarget(u);
  }
  const revokeMutation = useRevokeExam();
  const reviewMutation = useReviewExamRequest();

  const loading = usersLoading || examsLoading || requestsLoading;
  const error =
    usersError ??
    examsError ??
    requestsError ??
    assignMutation.error ??
    revokeMutation.error ??
    reviewMutation.error ??
    suspendMutation.error ??
    null;

  const busyKey =
    assignMutation.isPending && assignMutation.variables
      ? `${assignMutation.variables.userID}:${assignMutation.variables.examSlug}`
      : revokeMutation.isPending && revokeMutation.variables
        ? `${revokeMutation.variables.userID}:${revokeMutation.variables.examSlug}`
        : reviewMutation.isPending && reviewMutation.variables
          ? `${reviewMutation.variables.userID}:${reviewMutation.variables.examSlug}`
          : null;

  const [pickerFor, setPickerFor] = useState<AdminUser | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  const [revokeConfirmText, setRevokeConfirmText] = useState("");

  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.role !== "admin") {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (!pickerFor) return;
    const fresh = users.find((u) => u.id === pickerFor.id);
    if (fresh && fresh !== pickerFor) setPickerFor(fresh);
  }, [users, pickerFor]);

  async function handleConfirmRevoke() {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync({
        userID: revokeTarget.userID,
        examSlug: revokeTarget.examSlug,
      });
      setRevokeTarget(null);
      setRevokeConfirmText("");
    } catch {
      // error surfaced via revokeMutation.error
    }
  }

  if (authLoading || !isAuthenticated || user?.role !== "admin") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const pickerUnassigned = pickerFor
    ? exams.filter((e) => !pickerFor.assigned_exams.includes(e.id))
    : [];
  const pickerBusyKey =
    pickerFor && busyKey?.startsWith(`${pickerFor.id}:`)
      ? busyKey.slice(pickerFor.id.length + 1)
      : null;

  const revokeBusy =
    !!revokeTarget &&
    busyKey === `${revokeTarget.userID}:${revokeTarget.examSlug}`;
  const revokeExpected = revokeTarget?.examName ?? "";
  const revokeConfirmed =
    revokeConfirmText.trim().length > 0 &&
    revokeConfirmText.trim() === revokeExpected.trim();

  return (
    <div className="mx-auto w-full max-w-5xl">
      <div className="mb-2 flex items-center gap-2">
        <Shield className="size-6 text-blue-600" />
        <h1 className="text-2xl font-bold sm:text-3xl">{t("admin.title")}</h1>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">{t("admin.desc")}</p>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          {createErrorMessage(error)}
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        <Link to="/admin/groups">
          <Card className="cursor-pointer transition-colors hover:border-primary/40 hover:bg-muted/30">
            <CardContent className="flex items-center gap-3 py-4">
              <Users className="size-5 text-primary" />
              <div>
                <p className="font-medium">그룹 관리</p>
                <p className="text-sm text-muted-foreground">
                  스터디 그룹 생성, 멤버, 이메일 초대
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-6 w-full flex-wrap sm:w-auto">
          <TabsTrigger value="users">
            <UserCog className="size-4" />
            {t("admin.tab_users")}
          </TabsTrigger>
          <TabsTrigger value="requests">
            <ClipboardList className="size-4" />
            {t("admin.tab_requests")}
            {requests.length > 0 && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 min-w-5 justify-center px-1.5 text-[10px]"
              >
                {requests.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="suspended">
            <Ban className="size-4" />
            {t("admin_block.tab_suspended")}
            {suspendedCount > 0 && (
              <Badge
                variant="destructive"
                className="ml-1 h-5 min-w-5 justify-center px-1.5 text-[10px]"
              >
                {suspendedCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="blocks">
            <Ban className="size-4" />
            {t("admin_block.tab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">{t("admin_users.title")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("admin_users.desc")}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((u) => {
                const unassignedCount = exams.filter(
                  (e) => !u.assigned_exams.includes(e.id),
                ).length;
                return (
                  <Card key={u.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-base">
                            {u.email}
                            {u.role === "admin" && (
                              <Badge className="gap-1 bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400">
                                <Shield className="size-3" />
                                {t("nav.admin_role")}
                              </Badge>
                            )}
                            {u.email_verified && (
                              <CheckCircle2 className="size-4 text-green-500" />
                            )}
                            {u.suspended && (
                              <Badge variant="destructive" className="gap-1">
                                <Ban className="size-3" />
                                {t("admin_block.suspended_badge")}
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="mt-1 text-xs">
                            {t("admin_users.assigned_count")}:{" "}
                            {u.assigned_exams.length}
                          </CardDescription>
                        </div>
                        <div className="flex shrink-0 flex-wrap justify-end gap-2">
                          {unassignedCount > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="cursor-pointer"
                              onClick={() => setPickerFor(u)}
                            >
                              <Plus className="size-4" />
                              {t("admin_users.assign")}
                            </Button>
                          )}
                          {u.role !== "admin" && u.id !== user?.id && (
                            <Button
                              size="sm"
                              variant={u.suspended ? "outline" : "ghost"}
                              className={
                                u.suspended
                                  ? "cursor-pointer"
                                  : "cursor-pointer text-destructive hover:bg-destructive/10 hover:text-destructive"
                              }
                              onClick={() => openSuspendDialog(u)}
                            >
                              <Ban className="size-4" />
                              {u.suspended
                                ? t("admin_block.unsuspend")
                                : t("admin_block.suspend")}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {u.assigned_exams.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          {t("admin_users.no_assignments")}
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {u.assigned_exams.map((slug) => {
                            const exam = exams.find((e) => e.id === slug);
                            const examName = exam?.name ?? slug;
                            return (
                              <div
                                key={slug}
                                className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium"
                              >
                                <span>{examName}</span>
                                <button
                                  className="cursor-pointer rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                                  disabled={busyKey === `${u.id}:${slug}`}
                                  onClick={() => {
                                    setRevokeConfirmText("");
                                    setRevokeTarget({
                                      userID: u.id,
                                      userEmail: u.email,
                                      examSlug: slug,
                                      examName,
                                    });
                                  }}
                                  aria-label={t("admin_users.revoke")}
                                >
                                  <X className="size-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="suspended">
          <SuspendedUsersPanel
            users={users}
            loading={usersLoading}
            onReinstate={openSuspendDialog}
          />
        </TabsContent>

        <TabsContent value="blocks">
          <IPBlockPanel />
        </TabsContent>

        <TabsContent value="requests">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">
              {t("admin_requests.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("admin_requests.desc")}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
              {t("admin_requests.empty")}
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => {
                const exam = examMap[req.exam_slug];
                const key = `${req.user_id}:${req.exam_slug}`;
                return (
                  <Card key={key}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <CardTitle className="text-base">
                            {req.user_email ?? req.user_id}
                          </CardTitle>
                          <div className="mt-2 flex items-center gap-2">
                            <Badge variant="secondary">
                              {exam?.name ?? req.exam_slug}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(req.requested_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="cursor-pointer"
                            disabled={busyKey === key}
                            onClick={() =>
                              reviewMutation.mutate({
                                userID: req.user_id,
                                examSlug: req.exam_slug,
                                data: { action: "deny" },
                              })
                            }
                          >
                            {t("admin_requests.deny")}
                          </Button>
                          <Button
                            size="sm"
                            className="cursor-pointer"
                            disabled={busyKey === key}
                            onClick={() =>
                              reviewMutation.mutate({
                                userID: req.user_id,
                                examSlug: req.exam_slug,
                                data: { action: "approve" },
                              })
                            }
                          >
                            {busyKey === key ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              t("admin_requests.approve")
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    {req.reason && (
                      <CardContent>
                        <p className="rounded bg-muted/50 p-3 text-sm text-muted-foreground">
                          {req.reason}
                        </p>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ExamPicker
        open={!!pickerFor}
        onOpenChange={(open) => {
          if (!open) setPickerFor(null);
        }}
        title={t("admin_users.pick_exam")}
        description={pickerFor?.email}
        exams={pickerUnassigned}
        categoryMap={categoryMap}
        busyKey={pickerBusyKey}
        actionLabel={t("admin_users.assign")}
        onPick={async (slug) => {
          if (!pickerFor) return;
          await assignMutation.mutateAsync({
            userID: pickerFor.id,
            examSlug: slug,
          });
        }}
      />

      <Dialog
        open={!!revokeTarget}
        onOpenChange={(open) => {
          if (!open && !revokeBusy) {
            setRevokeTarget(null);
            setRevokeConfirmText("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="size-5" />
              {t("admin_users.revoke_confirm_title")}
            </DialogTitle>
            <DialogDescription>
              {t("admin_users.revoke_confirm_desc")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-semibold text-destructive">
                {t("admin_users.revoke_warning_title")}
              </p>
              <p className="mt-1 text-muted-foreground">
                {t("admin_users.revoke_warning_body")}
              </p>
            </div>

            {revokeTarget && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">
                    {t("admin_users.revoke_user_label")}
                  </span>
                  <p className="font-medium">{revokeTarget.userEmail}</p>
                </div>
                <div className="mt-2">
                  <span className="text-xs text-muted-foreground">
                    {t("admin_users.revoke_exam_label")}
                  </span>
                  <p className="font-medium">{revokeTarget.examName}</p>
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="revoke-confirm"
                className="text-xs text-muted-foreground"
              >
                {t("admin_users.revoke_type_prompt")}{" "}
                <span className="font-semibold text-foreground">
                  {revokeExpected}
                </span>
              </label>
              <input
                id="revoke-confirm"
                type="text"
                value={revokeConfirmText}
                onChange={(e) => setRevokeConfirmText(e.target.value)}
                disabled={revokeBusy}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <DialogClose
              render={
                <Button
                  variant="outline"
                  disabled={revokeBusy}
                  className="cursor-pointer"
                />
              }
            >
              {t("admin_users.revoke_cancel")}
            </DialogClose>
            <Button
              variant="destructive"
              disabled={!revokeConfirmed || revokeBusy}
              onClick={handleConfirmRevoke}
              className="cursor-pointer"
            >
              {revokeBusy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                t("admin_users.revoke_confirm_button")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!suspendTarget}
        onOpenChange={(open) => {
          if (!open && !suspendMutation.isPending) setSuspendTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="size-5 text-destructive" />
              {suspendTarget?.suspended
                ? t("admin_block.unsuspend_confirm_title")
                : t("admin_block.suspend_confirm_title")}
            </DialogTitle>
            <DialogDescription className="break-keep">
              <span className="font-medium text-foreground">{suspendTarget?.email}</span>
              <br />
              {suspendTarget?.suspended
                ? t("admin_block.unsuspend_confirm_desc")
                : t("admin_block.suspend_confirm_desc")}
            </DialogDescription>
          </DialogHeader>
          {!suspendTarget?.suspended && (
            <div className="space-y-1.5">
              <label htmlFor="suspend-reason" className="text-sm font-medium">
                {t("admin_block.suspend_reason")}
              </label>
              <input
                id="suspend-reason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder={t("admin_block.suspend_reason_placeholder")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                autoComplete="off"
              />
            </div>
          )}
          <DialogFooter>
            <DialogClose
              render={
                <Button
                  variant="outline"
                  disabled={suspendMutation.isPending}
                  className="cursor-pointer"
                />
              }
            >
              {t("common.cancel")}
            </DialogClose>
            <Button
              variant={suspendTarget?.suspended ? "default" : "destructive"}
              disabled={suspendMutation.isPending}
              className="cursor-pointer"
              onClick={() => {
                if (!suspendTarget) return;
                suspendMutation.mutate(
                  {
                    userID: suspendTarget.id,
                    suspended: !suspendTarget.suspended,
                    reason: suspendReason.trim(),
                  },
                  { onSettled: () => setSuspendTarget(null) },
                );
              }}
            >
              {suspendMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : suspendTarget?.suspended ? (
                t("admin_block.unsuspend")
              ) : (
                t("admin_block.suspend")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
